/**
 * pxm-attribution.js — first-touch marketing attribution capture.
 *
 * WHY: www.pixelmade.ai (this Shopify storefront) and app.pixelmade.ai (the Rails brand
 * portal, which owns the users table) are two separate programs on two separate servers.
 * A visitor arrives from Google onto an article, browses, then clicks through to sign up —
 * and by then `document.referrer` reads "www.pixelmade.ai", so the original source and the
 * page that actually earned the visit are gone. Measured 2026-07-29: 67 of 67
 * `merchant_signed_up` events say source "organic", because the app's existing detector
 * only recognises PAID click-ids and defaults everything else to that word.
 *
 * WHAT: capture the first touch ONCE, remember it while the visitor browses, and hand it to
 * the app as a query parameter on outbound sign-up links.
 *
 *   capture  — first pageview only; never overwritten (first-touch attribution: credit the
 *              page that introduced them, not the last one they happened to be on).
 *   remember — a first-party cookie on the parent domain, 90 days.
 *   forward  — decorate links to DECORATE_HOSTS with one compact `pxm_a` parameter.
 *
 * PRIVACY: the payload is anonymous — no name, email, id, or IP. It is a note about where a
 * BROWSER came from, and stays unattached to any person unless that person chooses to sign
 * up. Gated on Shopify's consent API when the store has one configured.
 *
 * DESIGN NOTE — store facts, derive labels later. We deliberately record the raw `referrer`
 * rather than a computed channel like "organic". Deriving too early is the exact bug in the
 * app today: it collapsed everything to one word and threw the evidence away, so the rows
 * can never be re-classified. Raw values can be re-labelled by a query forever.
 *
 * PORTABLE: no dependencies, no theme-specific assumptions. To reuse elsewhere (knitwise.com,
 * the customizer iframe), copy this file and edit CONFIG.
 */
(function () {
  'use strict';

  var CONFIG = {
    cookie: 'pxm_attr',
    days: 90,
    domain: '.pixelmade.ai', // parent domain — readable by www. and app. subdomains
    decorateHosts: ['app.pixelmade.ai'],
    param: 'pxm_a',
    version: 1,
  };

  var UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  var CLICK_IDS = ['gclid', 'fbclid', 'msclkid'];

  function readCookie(name) {
    var match = ('; ' + document.cookie).split('; ' + name + '=');
    if (match.length !== 2) return null;
    try {
      return JSON.parse(decodeURIComponent(match.pop().split(';').shift()));
    } catch (e) {
      return null;
    }
  }

  function writeCookie(name, value) {
    var expires = new Date(Date.now() + CONFIG.days * 864e5).toUTCString();
    document.cookie =
      name + '=' + encodeURIComponent(JSON.stringify(value)) +
      ';expires=' + expires + ';path=/;domain=' + CONFIG.domain + ';samesite=lax;secure';
  }

  /** Shopify Customer Privacy API, when the store has consent configured. Absent => allowed. */
  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed();
      }
    } catch (e) {
      /* fall through */
    }
    return true;
  }

  /** Build the first-touch payload from the CURRENT pageview. */
  function buildPayload() {
    var params = new URLSearchParams(window.location.search);
    var payload = {
      landing_path: window.location.pathname || '/',
      referrer: document.referrer || '',
      first_seen_at: new Date().toISOString(),
      capture_version: CONFIG.version,
    };
    UTM_KEYS.concat(CLICK_IDS).forEach(function (key) {
      var value = params.get(key);
      if (value) payload[key] = value.slice(0, 200); // bound the payload
    });
    return payload;
  }

  /** Capture once. Returns the stored payload (existing or newly written), or null. */
  function capture() {
    var existing = readCookie(CONFIG.cookie);
    if (existing) return existing; // FIRST touch wins — never overwrite
    if (!analyticsAllowed()) return null;
    var payload = buildPayload();
    writeCookie(CONFIG.cookie, payload);
    return payload;
  }

  function isDecorateTarget(anchor) {
    if (!anchor || !anchor.hostname || !anchor.href) return false;
    if (anchor.protocol !== 'http:' && anchor.protocol !== 'https:') return false;
    return CONFIG.decorateHosts.indexOf(anchor.hostname) !== -1;
  }

  /** Append the payload to an outbound sign-up link. Idempotent. */
  function decorate(anchor, payload) {
    if (!isDecorateTarget(anchor) || !payload) return;
    try {
      var url = new URL(anchor.href);
      if (url.searchParams.has(CONFIG.param)) return; // already decorated
      url.searchParams.set(CONFIG.param, JSON.stringify(payload));
      anchor.href = url.toString();
    } catch (e) {
      /* leave the link untouched rather than break navigation */
    }
  }

  function decorateAll(payload) {
    var anchors = document.querySelectorAll('a[href]');
    for (var i = 0; i < anchors.length; i++) decorate(anchors[i], payload);
  }

  function init() {
    var payload = capture();
    if (!payload) return;

    decorateAll(payload);

    // Safety net for links rendered after load (sections, drawers, app blocks): decorate
    // just-in-time on the way out. Capture phase so it runs before any other handler.
    document.addEventListener(
      'click',
      function (event) {
        var anchor = event.target && event.target.closest && event.target.closest('a[href]');
        if (anchor) decorate(anchor, payload);
      },
      true
    );

    // Expose for debugging + future callers (e.g. a beacon, or a form hidden field).
    window.pxmAttribution = {
      get: function () {
        return readCookie(CONFIG.cookie);
      },
      config: CONFIG,
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
